import { useState, memo, ReactNode, useCallback, useRef, useLayoutEffect } from "react";
import { motion, PanInfo } from "framer-motion";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";
import testimonial1 from "@/assets/testimonial-1.webp";
import testimonial2 from "@/assets/testimonial-2.webp";
import testimonial3 from "@/assets/testimonial-3.webp";
import testimonial4 from "@/assets/testimonial-4.webp";
import testimonial5 from "@/assets/testimonial-5.webp";
import testimonial6 from "@/assets/testimonial-6.webp";
import testimonial7 from "@/assets/testimonial-7.webp";
import testimonial8 from "@/assets/testimonial-8.webp";
import testimonial9 from "@/assets/testimonial-9.webp";
import testimonial10 from "@/assets/testimonial-10.webp";
import testimonial11 from "@/assets/testimonial-11.webp";
import testimonial12 from "@/assets/testimonial-12.webp";

interface Testimonial {
  quote: string;
  author: string;
  location: string;
  rating: number;
  image: string;
  imagePosition?: string;
}

const testimonials: Testimonial[] = [
  {
    quote: "My family had a wonderful day! We were greeted with warm friendly smiles and enjoyed our two story cabana. We swam, floated and cooled off in the river. We grilled lunch on the exceptionally clean grill provided. Rested in the hammock. Played a few rounds of cornhole. We can't wait to spend another day here!",
    author: "Sheryl Elizabeth",
    location: "via Google",
    rating: 5,
    image: testimonial1,
  },
  {
    quote: "We came for a quick getaway and didn't really expect too much for the price — and we were pleasantly surprised! Rooms were very clean, plenty of bathrooms and shower trailers and they were kept clean. The beach area has cabanas and covered tables and plenty of room to stretch out. The grounds were well kept and the service was quick and friendly.",
    author: "Taci Netherton",
    location: "via Google",
    rating: 5,
    image: testimonial2,
  },
  {
    quote: "I grew up tubing on the Guadalupe and wanted to share that experience with my three sons. Son's Blue River Camp was very family friendly compared to others on River Road. We'll be back every summer.",
    author: "Kristi & Michael R.",
    location: "Houston",
    rating: 5,
    image: testimonial3,
  },
  {
    quote: "The glamping cabin had everything we needed — AC, kitchenette, comfy beds. It was so nice to relax in comfort after a fun day on the river. The kids are already asking when we can come back!",
    author: "Amanda & Pete K.",
    location: "Dallas",
    rating: 5,
    image: testimonial4,
  },
  {
    quote: "Perfect getaway with our little one! The property is so well-maintained and family-friendly. We loved lounging in the hammocks while our toddler played nearby. Already planning our next trip!",
    author: "Jessica & David L.",
    location: "Round Rock",
    rating: 5,
    image: testimonial5,
  },
  {
    quote: "Best tubing experience on the San Marcos! The water was crystal clear and the float was so relaxing. The staff made sure we had everything we needed. Can't wait to come back!",
    author: "Chris P.",
    location: "New Braunfels",
    rating: 5,
    image: testimonial6,
  },
  {
    quote: "Brought the whole crew out for a weekend and it was amazing! Great vibes, cold drinks, beautiful river views. This place knows how to do summer right.",
    author: "Marcus W.",
    location: "Austin",
    rating: 5,
    image: testimonial7,
  },
  {
    quote: "We loved the peaceful atmosphere and the beautiful grounds. The glamping tents in the background made for such a unique experience. Great escape from the city!",
    author: "Jennifer & Matt S.",
    location: "San Marcos",
    rating: 5,
    image: testimonial8,
  },
  {
    quote: "Finally found a river spot where I can relax with my baby and still enjoy the water. The shallow areas are perfect for little ones. Mom-approved!",
    author: "Brittany H.",
    location: "Kyle",
    rating: 5,
    image: testimonial9,
    imagePosition: "top",
  },
  {
    quote: "The kids had the time of their lives! They didn't want to leave the river. Such a safe and fun environment for families. We're making this our annual tradition.",
    author: "The Rodriguez Family",
    location: "Seguin",
    rating: 5,
    image: testimonial10,
  },
  {
    quote: "We celebrated our anniversary here and it was magical. The sunset views from our cabana were incredible. Romantic yet still fun and adventurous!",
    author: "Nicole & Ryan B.",
    location: "Dripping Springs",
    rating: 5,
    image: testimonial3,
  },
  {
    quote: "Our whole crew comes here every year now. The group tubing trip is always a highlight. Easy parking, friendly staff, and the best stretch of river around.",
    author: "The Ramirez Crew",
    location: "Pflugerville",
    rating: 5,
    image: testimonial11,
  },
  {
    quote: "I was skeptical about glamping but this place converted me. All the comforts of home with nature right outside your door. Woke up to birds singing and fell asleep to river sounds.",
    author: "Laura M.",
    location: "Georgetown",
    rating: 5,
    image: testimonial2,
  },
  {
    quote: "Excellent place to take the family on a little camping trip. The river was cool and refreshing. Floated and also kayaked down the river. Sleeping arrangements are good and so are the restroom and shower trailers.",
    author: "Alberto Romero",
    location: "via Google",
    rating: 5,
    image: testimonial12,
  },
];

/**
 * TestimonialsSection - A fluid touch-swipe testimonial carousel
 * Uses the "Sliding Track Pattern" for smooth swipe animations
 */
const TestimonialsSection = memo(() => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Measure container width
  useLayoutEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Calculate wrapped indices
  const getWrappedIndex = useCallback((offset: number) => {
    const len = testimonials.length;
    return ((currentIndex + offset) % len + len) % len;
  }, [currentIndex]);

  const prevIndex = getWrappedIndex(-1);
  const nextIndex = getWrappedIndex(1);

  // Navigation
  const goToPrevious = useCallback(() => {
    setCurrentIndex(prevIndex);
  }, [prevIndex]);

  const goToNext = useCallback(() => {
    setCurrentIndex(nextIndex);
  }, [nextIndex]);

  // Drag handlers
  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDrag = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      setDragOffset(info.offset.x);
    },
    []
  );

  const handleDragEnd = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const { offset, velocity } = info;
      const threshold = 0.2;
      const momentumThreshold = 10000;
      
      const momentum = offset.x * velocity.x;
      const offsetPercent = containerWidth > 0 ? Math.abs(offset.x) / containerWidth : 0;

      setIsDragging(false);
      setDragOffset(0);

      if (offsetPercent > threshold || Math.abs(momentum) > momentumThreshold) {
        if (offset.x > 0) {
          goToPrevious();
        } else {
          goToNext();
        }
      }
    },
    [containerWidth, goToPrevious, goToNext]
  );

  // Calculate transform
  const baseTransform = -containerWidth;
  const currentTransform = baseTransform + dragOffset;

  // Slides to render
  const slidesToRender = [
    { index: prevIndex, testimonial: testimonials[prevIndex] },
    { index: currentIndex, testimonial: testimonials[currentIndex] },
    { index: nextIndex, testimonial: testimonials[nextIndex] },
  ];

  // Render a testimonial card
  const renderTestimonialCard = (testimonial: Testimonial) => (
    <div className="grid md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 items-center">
      {/* Guest Photo */}
      <div>
        <img 
          src={testimonial.image} 
          alt={`${testimonial.author}'s experience`}
          className="w-full h-48 sm:h-64 md:h-80 object-cover rounded-2xl pointer-events-none"
          style={{ objectPosition: testimonial.imagePosition || 'center' }}
          loading="lazy"
          decoding="async"
          width={400}
          height={320}
          draggable={false}
        />
      </div>

      {/* Content */}
      <div>
        {/* Stars */}
        <div className="flex gap-1 mb-3 sm:mb-4 md:mb-6">
          {[...Array(testimonial.rating)].map((_, i) => (
            <Star key={i} className="h-4 w-4 sm:h-5 sm:w-5 fill-golden text-golden" />
          ))}
        </div>

        {/* Quote */}
        <blockquote className="font-body text-sm sm:text-base md:text-lg lg:text-xl text-foreground leading-relaxed mb-4 sm:mb-6">
          "{testimonial.quote}"
        </blockquote>

        {/* Author */}
        <div>
          <p className="font-heading font-semibold text-base sm:text-lg text-foreground">
            {testimonial.author}
          </p>
          <p className="text-sm sm:text-base text-muted-foreground">
            {testimonial.location}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <section className="py-24 bg-gradient-to-b from-river/10 to-river/5 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-river/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-golden/5 rounded-full translate-x-1/2 translate-y-1/2" />

      <div className="container mx-auto px-4 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-brown mb-4">
            Don't Take Our Word For It
          </h2>
          <p className="text-lg text-muted-foreground">
            See what Texas families are saying about their Son's Blue River Camp experience.
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div className="max-w-5xl mx-auto">
          <div className="relative bg-card rounded-3xl p-4 sm:p-6 md:p-10 lg:p-12 shadow-elevated border border-border group">
            {/* Quote Icon */}
            <div className="absolute -top-6 left-8 w-12 h-12 rounded-full bg-golden flex items-center justify-center z-20">
              <Quote className="h-6 w-6 text-brown" />
            </div>

            {/* Track Wrapper - measures visible width, clips overflow */}
            <div ref={containerRef} className="overflow-hidden">
              {/* Sliding Track */}
              <motion.div
                className="flex cursor-grab active:cursor-grabbing"
                style={{
                  width: containerWidth > 0 ? `${containerWidth * 3}px` : '300%',
                  transform: containerWidth > 0 ? `translateX(${currentTransform}px)` : 'translateX(-100%)',
                  transition: isDragging ? "none" : "transform 300ms ease-out",
                  willChange: isDragging ? "transform" : "auto",
                }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.1}
                onDragStart={handleDragStart}
                onDrag={handleDrag}
                onDragEnd={handleDragEnd}
              >
                {slidesToRender.map((slide, i) => (
                  <div
                    key={`testimonial-${i}-${slide.index}`}
                    className="flex-shrink-0 select-none"
                    style={{ width: containerWidth > 0 ? containerWidth : '100%' }}
                  >
                    {renderTestimonialCard(slide.testimonial)}
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-4 sm:mt-6 md:mt-8 relative z-10">
              {/* Mobile: Simple counter | Desktop: Dots */}
              <div className="flex items-center">
                {/* Mobile counter */}
                <span className="sm:hidden text-sm text-muted-foreground font-medium">
                  {currentIndex + 1} / {testimonials.length}
                </span>
                
                {/* Desktop dots */}
                <div className="hidden sm:flex gap-2 overflow-x-auto max-w-[50%] scrollbar-hide">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`w-5 h-5 md:w-6 md:h-6 rounded-full transition-all flex items-center justify-center flex-shrink-0 ${
                        index === currentIndex
                          ? "bg-river"
                          : "bg-transparent hover:bg-muted"
                      }`}
                      aria-label={`Go to testimonial ${index + 1}`}
                    >
                      <span className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${
                        index === currentIndex ? "bg-cream" : "bg-border"
                      }`} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Arrows */}
              <div className="flex gap-1.5 sm:gap-2">
                <button
                  onClick={goToPrevious}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-border hover:border-river hover:bg-river/5 flex items-center justify-center transition-all"
                  aria-label="Previous testimonial"
                >
                  <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 text-foreground" />
                </button>
                <button
                  onClick={goToNext}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-border hover:border-river hover:bg-river/5 flex items-center justify-center transition-all"
                  aria-label="Next testimonial"
                >
                  <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-foreground" />
                </button>
              </div>
            </div>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-6">
            Reviews sourced from Google. See all reviews on our{" "}
            <a href="https://g.page/r/sonsblueriver" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
              Google listing
            </a>
            .
          </p>
        </div>
      </div>
    </section>
  );
});

TestimonialsSection.displayName = "TestimonialsSection";

export default TestimonialsSection;
